/** @jsxImportSource preact */
import * as m from "../../paraglide/messages.js";
import { formVars } from "../form/form-styles";
import type { WidgetTokens } from "../theme";
import { OTPField } from "./base-ui.mjs";

/** 店铺发出的一次性验证码位数。 */
export const VERIFICATION_CODE_LENGTH = 6;

/**
 * 验证码井和格子的样式。宿主要把它放进同一个 Shadow DOM，
 * 颜色和圆角从组件根节点上的 --gn-form-* 变量读（formVars），跟着商家主题走；
 * 这一段自给自足，单独摆这个字段时不用再带 FORM_STYLES。
 *
 * 三种格子：填了的是白色圆角方块；正在输入的（聚焦）是墨黑实心，空格里画一根闪烁的反色光标；
 * 还没填的是虚线框。出错不只靠颜色：格子换成实线危险色描边。
 */
export const VERIFICATION_CODE_STYLES = `
.gn-code { display:flex; flex-direction:column; gap:6px; min-width:0; }
.gn-code-label { font-size:12px; font-weight:var(--gn-form-strong); color:var(--gn-form-quiet); letter-spacing:0; }
.gn-code-well { box-sizing:border-box; display:flex; align-items:center; gap:6px; padding:8px; border-radius:var(--gn-form-r-well); background:var(--gn-form-sheet); }
.gn-code-slot { box-sizing:border-box; flex:1 1 0; min-width:0; height:48px; padding:0; border:1.5px solid transparent; border-radius:var(--gn-form-r-cell); background:var(--gn-form-chip); color:var(--gn-form-ink); font:inherit; font-size:18px; font-weight:700; font-variant-numeric:tabular-nums; text-align:center; caret-color:var(--gn-form-answer-fg); outline:none; transition:background .15s, color .15s; }
.gn-code-slot:not([data-filled]) { border-style:dashed; border-color:var(--gn-form-dash); background:transparent; }
.gn-code-slot:focus { border-color:transparent; background:var(--gn-form-answer); color:var(--gn-form-answer-fg); }
.gn-code-slot:focus:not([data-filled]) { caret-color:transparent; background-image:linear-gradient(var(--gn-form-answer-fg), var(--gn-form-answer-fg)); background-repeat:no-repeat; background-position:center; background-size:2px 20px; animation:gn-code-blink 1.1s steps(1) infinite; }
@keyframes gn-code-blink { 50% { background-size:0 0; } }
.gn-code-slot[aria-invalid="true"] { border-style:solid; border-color:var(--gn-form-danger); }
.gn-code-slot:disabled { opacity:.55; cursor:not-allowed; }
@media (prefers-reduced-motion: reduce) { .gn-code-slot { transition:none; animation:none !important; } }
`;

export interface VerificationCodeFieldProps {
  /** 第一个格子的 id；label 指向它，后面的格子由 Base UI 依次派生。 */
  id: string;
  tokens: WidgetTokens;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** 六位填满（包括整段粘贴、一次性验证码自动填充）时调用。 */
  onValueComplete?: (value: string) => void;
  /** 验证码不对或过期：格子换成危险色实线描边，读屏报告无效。 */
  invalid?: boolean;
  /** 字段下方提示或错误的 id。 */
  describedBy?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}

/**
 * 邮箱验证码输入：六个格子并排放进一口内凹的井里。
 * 底层是 Base UI 的 OTPField：逐格输入、退格回到上一格、整段粘贴和
 * autocomplete="one-time-code" 自动填充都由它处理，只收数字，手机弹数字键盘。
 */
export function VerificationCodeField({
  id,
  tokens: t,
  value,
  defaultValue,
  onValueChange,
  onValueComplete,
  invalid = false,
  describedBy,
  disabled = false,
  autoFocus = false,
}: VerificationCodeFieldProps) {
  return (
    <div class="gn-code" style={formVars(t)}>
      <label class="gn-code-label" for={id}>
        {m.widget_verification_code_label()}
      </label>
      <OTPField.Root
        className="gn-code-well"
        defaultValue={defaultValue}
        disabled={disabled}
        id={id}
        length={VERIFICATION_CODE_LENGTH}
        onValueChange={(next) => onValueChange?.(next)}
        onValueComplete={(next) => onValueComplete?.(next)}
        value={value}
      >
        {Array.from({ length: VERIFICATION_CODE_LENGTH }, (_, index) => (
          <OTPField.Input
            key={index}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            aria-label={m.widget_verification_code_digit({
              index: String(index + 1),
              length: String(VERIFICATION_CODE_LENGTH),
            })}
            autoFocus={autoFocus && index === 0}
            className="gn-code-slot"
          />
        ))}
      </OTPField.Root>
    </div>
  );
}
